import React, { useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

const PAGE_SIZE = 100;
const NEW_CARD_KEY = '__new_card__';
const DETAIL_PANEL_DEFAULT_WIDTH = () => clampDetailPanelWidth(Math.round(window.innerWidth * 0.70));

const VOCAB_FAMILIES = [
  'sector',
  'category_type',
  'category',
  'utility_kind',
  'seasonality',
  'budget',
  'booking_logic',
  'weather_logic',
  'parking_access',
  'fact_warning_label',
];

const CARD_FIELDS = [
  'card_title',
  'retrieval_lane',
  'pitch',
  'caveat',
  'card_note_for_agent',
  'category',
  'guest_use_case_tags',
  'best_for',
  'booking_logic',
  'seasonality',
  'weather_logic',
  'budget',
  'parking_access',
  'utility_kind',
  'practical_note',
  'confidence',
  'freshness_risk',
  'admin_notes',
];

const PLACE_FIELDS = [
  'name',
  'sector_primary',
  'category_type',
  'area',
  'town_or_community',
  'address',
  'operator_review_note',
  'eligible_for_pocket_plan',
  'eligible_for_companion',
];

const FRESHNESS_RISK_OPTIONS = [
  'low',
  'medium',
  'high',
  'event_date_required',
  'seasonal_hours_required',
  'source_stale',
  'unknown',
];

const RETRIEVAL_LANE_OPTIONS = [
  'recommendation',
  'practical_find',
  'companion_practical',
  'local_directory',
  'do_not_retrieve',
];

const STAGE_META = {
  needs_work: { label: 'Needs work', badge: 'warning', reason: 'Fix required fields or blockers' },
  ready_to_approve: { label: 'Ready to approve', badge: 'accent', reason: 'Awaiting your review' },
  attention: { label: 'Attention', badge: 'danger', reason: 'Live or approved card needs attention' },
  approved: { label: 'Approved', badge: 'neutral', reason: 'Ready to publish' },
  published: { label: 'Published', badge: 'success', reason: 'Live' },
  inactive: { label: 'Archived / retired', badge: 'neutral', reason: 'Archived or retired' },
};

const SAVED_VIEW_PRESETS = [
  {
    id: 'all',
    label: 'All',
    filters: {},
    columns: ['place', 'stage', 'reason', 'verified', 'inbox'],
    sort: { field: 'priority_score', direction: 'desc' },
  },
  {
    id: 'needs_work',
    label: STAGE_META.needs_work.label,
    filters: { stage: 'needs_work' },
    columns: ['place', 'stage', 'reason', 'verified', 'inbox'],
    sort: { field: 'priority_score', direction: 'desc' },
  },
  {
    id: 'ready_to_approve',
    label: STAGE_META.ready_to_approve.label,
    filters: { stage: 'ready_to_approve' },
    columns: ['place', 'stage', 'reason', 'verified', 'inbox'],
    sort: { field: 'priority_score', direction: 'desc' },
  },
  {
    id: 'attention',
    label: STAGE_META.attention.label,
    filters: { stage: 'attention' },
    columns: ['place', 'stage', 'reason', 'verified', 'inbox'],
    sort: { field: 'priority_score', direction: 'desc' },
  },
  {
    id: 'approved',
    label: STAGE_META.approved.label,
    filters: { stage: 'approved' },
    columns: ['place', 'stage', 'reason', 'verified', 'inbox'],
    sort: { field: 'priority_score', direction: 'desc' },
  },
  {
    id: 'published',
    label: STAGE_META.published.label,
    filters: { stage: 'published' },
    columns: ['place', 'stage', 'reason', 'verified', 'inbox'],
    sort: { field: 'priority_score', direction: 'desc' },
  },
  {
    id: 'inactive',
    label: STAGE_META.inactive.label,
    filters: { stage: 'inactive' },
    columns: ['place', 'stage', 'reason', 'verified', 'inbox'],
    sort: { field: 'priority_score', direction: 'desc' },
  },
];

const WORKER_COMMANDS = [
  { id: 'scrape_site', label: 'Scrape site', scope: 'place' },
  { id: 'refresh_sources', label: 'Refresh sources', scope: 'place' },
  { id: 'duplicate_check', label: 'Duplicate check', scope: 'place' },
  { id: 'detect_opportunities', label: 'Detect opportunities', scope: 'place' },
  { id: 'refresh_event_data', label: 'Refresh event data', scope: 'card' },
  { id: 'enrich_card', label: 'Enrich card', scope: 'card' },
  { id: 'enrich_knows', label: 'Enrich Knows', scope: 'card' },
  { id: 'check_freshness', label: 'Check freshness', scope: 'card' },
];

function text(value) {
  return String(value || '').trim();
}

function labelize(value) {
  return text(value).replace(/_/g, ' ') || '-';
}

function optionValue(option) {
  return typeof option === 'string' ? option : option?.value || '';
}

function optionLabel(option) {
  return typeof option === 'string' ? labelize(option) : option?.label || labelize(option?.value);
}

function arrayValue(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return String(value).split(/[,|\n]/).map(item => item.trim()).filter(Boolean);
}

function sameValue(left, right) {
  const leftValue = Array.isArray(left) ? [...left].sort() : left || '';
  const rightValue = Array.isArray(right) ? [...right].sort() : right || '';
  return JSON.stringify(leftValue) === JSON.stringify(rightValue);
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function readiness(card) {
  const data = card?.publish_readiness || {};
  const missing = data.missing || [];
  const blockers = data.blockers || [];
  return {
    publishable: Boolean(data.publishable),
    missing,
    blockers,
    taxonomyReview: data.taxonomy_review || {},
    issueCount: missing.length + blockers.length,
  };
}

function cardStage(card) {
  return card?.stage || (card?.is_archived || card?.archived_at ? 'inactive' : null);
}

function stageMeta(stage) {
  return STAGE_META[stage] || { label: labelize(stage || 'unknown'), badge: 'neutral', reason: 'Needs review' };
}

function stageLabel(card) {
  return stageMeta(cardStage(card)).label;
}

function stageBadgeKind(card) {
  return stageMeta(cardStage(card)).badge;
}

function stageReason(card) {
  const reasons = card?.stage_reasons || [];
  if (reasons.length > 0) {
    const state = readiness(card);
    const issue = state.missing[0] || state.blockers[0];
    const taxonomyDetail = taxonomyDetailForIssue(issue, state.taxonomyReview);
    return taxonomyDetail ? `${reasons[0]} - ${taxonomyDetail}` : reasons[0];
  }
  const stage = cardStage(card);
  if (stage === 'published' && card?.live_version) return `Live · v${card.live_version}`;
  if (stage === 'approved' && card?.approved_version) {
    return card?.live_version ? `Approved v${card.approved_version} · live v${card.live_version}` : `Approved v${card.approved_version}`;
  }
  return stageMeta(stage).reason;
}

function cardIdentity(card) {
  return `${card?.place_master_id || ''}:${card?.card_key || ''}`;
}

function cardInboxCount(card) {
  return card?.inbox_count || card?.pending_change_request_count || card?.pending_signal_proposal_count || 0;
}

function cardBlockingProposalCount(card) {
  return card?.blocking_proposal_count || 0;
}

function getSavedView(views, id) {
  return views.find(view => view.id === id) || views[0];
}

function cardMatchesSavedView(card, view) {
  const filters = view?.filters || {};
  if (filters.stage && cardStage(card) !== filters.stage) return false;
  if (filters.inbox === 'active' && cardInboxCount(card) <= 0) return false;
  return true;
}

function savedViewQueryParams(view) {
  const filters = view?.filters || {};
  const params = {};
  if (filters.admin_review_status) params.status = filters.admin_review_status;
  if (filters.stage) params.stage = filters.stage;
  return params;
}

function detailPanelBounds() {
  if (typeof window === 'undefined') return { min: 720, max: 1080 };
  const min = Math.min(720, Math.max(340, window.innerWidth - 96));
  const max = Math.max(min, window.innerWidth - 96);
  return { min, max };
}

function clampDetailPanelWidth(width) {
  const { min, max } = detailPanelBounds();
  return Math.max(min, Math.min(max, width));
}

function isEditableShortcutTarget(target) {
  const tagName = target?.tagName?.toLowerCase();
  return Boolean(
    target?.isContentEditable
    || tagName === 'input'
    || tagName === 'textarea'
    || tagName === 'select'
  );
}

function primaryBlocker(card) {
  return stageReason(card);
}

function badgeClass(kind) {
  if (kind === 'success') return 'bg-green-50 text-green-700 border-green-200';
  if (kind === 'warning') return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  if (kind === 'danger') return 'bg-red-50 text-red-700 border-red-200';
  if (kind === 'accent') return 'bg-blue-50 text-blue-700 border-blue-200';
  if (kind === 'purple') return 'bg-purple-50 text-purple-700 border-purple-200';
  return 'bg-stone-100 text-stone-600 border-stone-200';
}

function formFromCard(card) {
  return {
    card_title: card?.card_title || card?.place_name || '',
    retrieval_lane: card?.retrieval_lane || 'recommendation',
    pitch: card?.pitch || card?.display_pitch || '',
    caveat: card?.caveat || '',
    card_note_for_agent: card?.card_note_for_agent || '',
    category: card?.card_category || card?.category || '',
    guest_use_case_tags: card?.guest_use_case_tags || [],
    best_for: card?.best_for || [],
    booking_logic: card?.booking_logic || [],
    seasonality: card?.seasonality || '',
    weather_logic: card?.weather_logic || [],
    budget: card?.budget || '',
    parking_access: card?.parking_access || [],
    utility_kind: card?.utility_kind || '',
    practical_note: card?.practical_note || '',
    confidence: card?.confidence || '',
    freshness_risk: card?.freshness_risk || '',
    admin_notes: card?.admin_notes || '',
  };
}

function newCardDraft(place, detail) {
  const summary = {
    card_key: NEW_CARD_KEY,
    card_title: 'New card',
    retrieval_lane: 'recommendation',
    category: place?.category_type || place?.sector_primary || detail?.card_category || detail?.category || '',
    card_category: place?.category_type || place?.sector_primary || detail?.card_category || detail?.category || '',
    card_status: 'draft',
    publish_status: 'draft',
    is_unsaved: true,
  };
  const cards = [
    ...(detail?.cards || place?.cards || []).filter(card => card.card_key !== NEW_CARD_KEY),
    summary,
  ];
  return {
    ...detail,
    ...summary,
    id: null,
    working_card_id: null,
    card_title: '',
    place_master_id: place?.place_master_id || detail?.place_master_id,
    place_name: place?.name || place?.place_name || detail?.place_name,
    pitch: '',
    display_pitch: '',
    caveat: '',
    card_note_for_agent: '',
    guest_use_case_tags: [],
    best_for: [],
    booking_logic: [],
    seasonality: '',
    weather_logic: [],
    budget: '',
    parking_access: [],
    utility_kind: '',
    practical_note: '',
    confidence: '',
    freshness_risk: '',
    admin_notes: detail?.admin_notes || '',
    admin_review_status: 'queued',
    agent_card_status: 'not_agent_ready',
    card_judgment_needed: true,
    published_version_id: null,
    published_item: null,
    versions: [],
    archived_at: null,
    is_archived: false,
    cards,
    card_count: cards.length,
  };
}

function formFromPlace(place) {
  return {
    name: place?.name || place?.place_name || '',
    sector_primary: place?.sector_primary || '',
    category_type: place?.category_type || '',
    area: place?.area || '',
    town_or_community: place?.town_or_community || '',
    address: place?.address || '',
    operator_review_note: place?.operator_review_note || '',
    eligible_for_pocket_plan: Boolean(place?.eligible_for_pocket_plan),
    eligible_for_companion: Boolean(place?.eligible_for_companion),
  };
}

function changedPayload(form, original, fields) {
  const payload = {};
  fields.forEach(field => {
    const next = form[field];
    const prev = field === 'name' ? (original?.name || original?.place_name || '') : original?.[field];
    if (!sameValue(next, prev)) payload[field] = next;
  });
  return payload;
}

function focusInputClass(active) {
  return active ? 'border-blue-500 ring-2 ring-blue-200' : '';
}

function TextInput({ label, value, onChange, placeholder = '', focusKey = null, highlight = false }) {
  return (
    <label className="block" data-focus={focusKey || undefined}>
      <span className="block text-xs font-medium text-stone-500 mb-1.5">{label}</span>
      <input
        value={value || ''}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900 ${focusInputClass(highlight)}`}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, placeholder = '', warning = false, focusKey = null, highlight = false, disabled = false }) {
  return (
    <label className="block" data-focus={focusKey || undefined}>
      <span className="block text-xs font-medium text-stone-500 mb-1.5">{label}</span>
      <textarea
        rows={rows}
        value={value || ''}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full rounded-lg border px-3 py-2 text-sm leading-6 focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-stone-100 disabled:text-stone-500 ${
          highlight
            ? 'border-blue-500 ring-2 ring-blue-200 focus:border-blue-700 focus:ring-blue-700'
            : warning
              ? 'border-yellow-400 focus:border-yellow-600 focus:ring-yellow-600'
              : 'border-stone-300 focus:border-stone-900 focus:ring-stone-900'
        }`}
      />
    </label>
  );
}

function SelectField({ label, value, options, onChange, required = false, focusKey = null, highlight = false, disabled = false }) {
  const values = new Set((options || []).map(optionValue));
  const merged = value && !values.has(value) ? [...(options || []), value] : (options || []);
  return (
    <label className="block" data-focus={focusKey || undefined}>
      <span className="block text-xs font-medium text-stone-500 mb-1.5">
        {label}{required && <span className="text-yellow-700"> · required</span>}
      </span>
      <select
        value={value || ''}
        onChange={event => onChange(event.target.value)}
        disabled={disabled}
        className={`w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900 disabled:cursor-not-allowed disabled:bg-stone-100 disabled:text-stone-500 ${focusInputClass(highlight)}`}
      >
        <option value="">-</option>
        {merged.map(option => (
          <option key={optionValue(option)} value={optionValue(option)}>{optionLabel(option)}</option>
        ))}
      </select>
    </label>
  );
}

function MultiPicker({ label, selected, options, onChange, accent = false }) {
  const values = Array.isArray(selected) ? selected : [];
  const optionValues = (options || []).map(optionValue).filter(Boolean);
  const merged = [...optionValues, ...values.filter(value => !optionValues.includes(value))];
  const toggle = value => {
    onChange(values.includes(value) ? values.filter(item => item !== value) : [...values, value]);
  };
  return (
    <div>
      <p className="text-xs font-medium text-stone-500 mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5 rounded-lg border border-stone-300 bg-white p-2">
        {merged.length === 0 && <span className="text-xs text-stone-400">No approved terms yet</span>}
        {merged.map(value => {
          const active = values.includes(value);
          return (
            <button
              type="button"
              key={value}
              onClick={() => toggle(value)}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                active
                  ? accent ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-green-200 bg-green-50 text-green-700'
                  : 'border-stone-200 bg-white text-stone-600 hover:border-stone-400'
              }`}
            >
              {labelize(value)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function displayDiffValue(value) {
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (value && typeof value === 'object') return JSON.stringify(value);
  if (value === true) return 'true';
  if (value === false) return 'false';
  return text(value) || '-';
}

function editableProposalValue(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value) || typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

function parseEditedProposalValue(raw, originalValue) {
  if (Array.isArray(originalValue) || (originalValue && typeof originalValue === 'object')) {
    try {
      return JSON.parse(raw || 'null');
    } catch (_e) {
      throw new Error('Edited structured proposal must be valid JSON.');
    }
  }
  if (typeof originalValue === 'boolean') {
    return ['true', 'yes', '1', 'y'].includes(String(raw).trim().toLowerCase());
  }
  if (typeof originalValue === 'number') {
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) throw new Error('Edited numeric proposal must be a number.');
    return parsed;
  }
  return raw;
}

function compareValue(value) {
  if (Array.isArray(value)) return JSON.stringify([...value].map(String).sort());
  if (value && typeof value === 'object') return JSON.stringify(value);
  return text(value);
}

// Mirrors backend-api/services/place_cards.py publish_card projection. Keep
// these fields aligned so the Review tab compares the same surface Composer reads.
const PUBLISHED_DIFF_FIELDS = [
  ['Card title', form => form.card_title, item => item.card_title],
  ['Pitch', form => form.pitch, item => item.pitch || item.short_guest_copy],
  ['Knows', form => form.caveat, item => item.caveat],
  ['Agent guidance', form => form.card_note_for_agent, item => item.agent_note],
  ['Card Category', form => form.category, item => item.category_type || item.sector_primary],
  ['Card Tags', form => form.guest_use_case_tags, item => item.tags],
  ['Best for', form => form.best_for, item => item.best_for],
  ['Booking logic', form => form.booking_logic, item => item.booking_logic || arrayValue(item.action_json?.booking_logic)],
  ['Seasonality', form => form.seasonality, item => item.seasonality],
  ['Weather logic', form => form.weather_logic, item => item.weather_logic],
  ['Budget', form => form.budget, item => item.budget_signal],
  ['Arrival & parking', form => form.parking_access, item => item.parking_access],
  ['Utility kind', form => form.utility_kind, item => item.utility_kind],
  ['Practical note', form => form.practical_note, item => item.practical_note],
  ['Confidence', form => form.confidence, item => item.confidence],
  ['Freshness risk', form => form.freshness_risk, item => item.freshness_risk],
];

function publishedProjectionDiffs(cardForm, publishedItem) {
  if (!publishedItem) return [];
  return PUBLISHED_DIFF_FIELDS
    .map(([label, currentValue, publishedValue]) => ({
      label,
      current: currentValue(cardForm),
      published: publishedValue(publishedItem),
    }))
    .filter(item => compareValue(item.current) !== compareValue(item.published));
}

function DiffRows({ changes, previousLabel = 'Previous' }) {
  if (!changes || changes.length === 0) return null;
  return (
    <div className="grid gap-2">
      {changes.map(change => {
        const previous = Object.prototype.hasOwnProperty.call(change, 'published') ? change.published : change.approved;
        const key = change.label || change.field;
        return (
          <div key={key} className="grid gap-2 rounded-lg border border-stone-200 bg-white p-3 text-sm md:grid-cols-[150px_1fr_1fr]">
            <p className="font-medium text-stone-700">{change.label || labelize(change.field)}</p>
            <div>
              <p className="mb-1 text-xs text-stone-400">{previousLabel}</p>
              <p className="text-stone-500">{displayDiffValue(previous)}</p>
            </div>
            <div>
              <p className="mb-1 text-xs text-stone-400">Working</p>
              <p className="text-stone-900">{displayDiffValue(change.current)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CardTags({ tags, selected, onChange, onCreate }) {
  const [query, setQuery] = useState('');
  const approved = (tags || []).filter(tag => tag.active && tag.vocabulary_status !== 'retired');
  const values = Array.isArray(selected) ? selected : [];
  const grouped = approved.reduce((acc, tag) => {
    const facet = tag.facet || 'ungrouped';
    if (!acc[facet]) acc[facet] = [];
    acc[facet].push(tag);
    return acc;
  }, {});
  const facetNames = Object.keys(grouped).sort((a, b) => (a === 'ungrouped' ? 1 : b === 'ungrouped' ? -1 : a.localeCompare(b)));
  const normalizedQuery = query.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  const existing = approved.some(tag => tag.name === normalizedQuery);
  const toggle = name => {
    onChange(values.includes(name) ? values.filter(item => item !== name) : [...values, name]);
  };
  const create = async () => {
    if (!normalizedQuery) return;
    const tag = await onCreate(normalizedQuery);
    const name = tag?.name || normalizedQuery;
    if (!values.includes(name)) onChange([...values, name]);
    setQuery('');
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-xs font-medium text-stone-500">Card Tags</p>
        <div className="flex items-center gap-2">
          <input
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); create(); } }}
            placeholder="Add pending tag"
            className="w-40 rounded-md border border-stone-300 px-2 py-1 text-xs focus:border-stone-900 focus:outline-none"
          />
          <button
            type="button"
            onClick={create}
            disabled={!normalizedQuery || existing}
            className="rounded-md border border-stone-300 px-2 py-1 text-xs text-stone-700 hover:bg-stone-50 disabled:opacity-40"
          >
            Add
          </button>
        </div>
      </div>
      <div className="grid gap-3 rounded-lg border border-stone-300 bg-white p-3">
        {facetNames.map(facet => (
          <div key={facet} className="grid grid-cols-[110px_1fr] gap-3">
            <p className="text-xs text-stone-500 pt-1">{facet === 'access' ? 'Site & water access' : labelize(facet)}</p>
            <div className="flex flex-wrap gap-1.5">
              {grouped[facet].map(tag => {
                const active = values.includes(tag.name);
                const pending = tag.vocabulary_status === 'pending';
                return (
                  <button
                    type="button"
                    key={tag.name}
                    onClick={() => toggle(tag.name)}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                      active
                        ? pending
                          ? 'border-dashed border-stone-400 bg-white text-stone-700'
                          : 'border-blue-200 bg-blue-50 text-blue-700'
                        : 'border-stone-200 bg-white text-stone-600 hover:border-stone-400'
                    }`}
                  >
                    {tag.display_label || labelize(tag.name)}{pending && ' · pending'}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {values.filter(value => !approved.some(tag => tag.name === value)).length > 0 && (
          <div className="grid grid-cols-[110px_1fr] gap-3">
            <p className="text-xs text-stone-500 pt-1">selected</p>
            <div className="flex flex-wrap gap-1.5">
              {values.filter(value => !approved.some(tag => tag.name === value)).map(value => (
                <button
                  type="button"
                  key={value}
                  onClick={() => toggle(value)}
                  className="rounded-full border border-dashed border-stone-400 px-2.5 py-1 text-xs font-medium text-stone-700"
                >
                  {labelize(value)} · pending
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Badge({ children, kind = 'neutral' }) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${badgeClass(kind)}`}>{children}</span>;
}

const READINESS_FIXES = {
  name: { scope: 'Place', tab: 'place', focus: 'place-name', action: 'Edit place name' },
  area: { scope: 'Place', tab: 'place', focus: 'place-area', action: 'Edit area' },
  trusted_source: { scope: 'Place', tab: 'place', focus: 'place-source', action: 'Add website/source' },
  missing_category: { scope: 'Card', tab: 'card', focus: 'card-category', action: 'Set Card Category' },
  utility_kind: { scope: 'Card', tab: 'card', focus: 'utility-kind', action: 'Set utility kind' },
  practical_note: { scope: 'Card', tab: 'card', focus: 'practical-note', action: 'Add practical note' },
  practical_action_path: { scope: 'Place', tab: 'place', focus: 'place-source', action: 'Add action path' },
  pitch: { scope: 'Card', tab: 'card', focus: 'pitch', action: 'Add pitch' },
  caveat: { scope: 'Card', tab: 'card', focus: 'caveat', action: 'Add Knows' },
  last_human_reviewed_at: { scope: 'Card', tab: 'review', focus: 'approve-publish', action: 'Review and approve' },
  card_not_approved: { scope: 'Card', tab: 'review', focus: 'approve-publish', action: 'Approve card' },
  card_judgment_needed: { scope: 'Card', tab: 'review', focus: 'approve-publish', action: 'Review and approve' },
  blocking_card_fact_warning: { scope: 'Card', tab: 'inbox', focus: 'proposal-tier-blocking', action: 'Open blocking proposal' },
  card_expired: { scope: 'Card', tab: 'card', focus: 'freshness-risk', action: 'Refresh or retire' },
  not_plan_or_companion_eligible: { scope: 'Place', tab: 'place', focus: 'place-eligibility', action: 'Review eligibility' },
  place_retired: { scope: 'Place', tab: 'place', focus: 'place-identity', action: 'Review retired place' },
  place_superseded: { scope: 'Place', tab: 'place', focus: 'place-identity', action: 'Review duplicate' },
  blocking_place_warning: { scope: 'Place', tab: 'inbox', focus: 'proposal-tier-blocking', action: 'Open blocking proposal' },
  blocking_card_signal: { scope: 'Card', tab: 'inbox', focus: 'proposal-tier-blocking', action: 'Open blocking proposal' },
  blocking_place_signal: { scope: 'Place', tab: 'inbox', focus: 'proposal-tier-blocking', action: 'Open blocking proposal' },
};

function readinessIssueInfo(issue, type, cardCount = 1, taxonomyReview = {}) {
  const taxonomyIssue = /^(pending|unapproved)_.+$/.test(String(issue || ''));
  const taxonomyStatus = taxonomyStatusForIssue(issue, taxonomyReview);
  const info = READINESS_FIXES[issue] || {
    scope: issue.startsWith('place_') || issue.includes('place') ? 'Place' : 'Card',
    tab: issue.startsWith('place_') || issue.includes('place') ? 'place' : 'review',
    action: taxonomyIssue ? taxonomyActionForStatus(taxonomyStatus) : type === 'missing' ? 'Fix' : 'Review',
  };
  return {
    ...info,
    label: type === 'missing' ? `Missing ${labelize(issue)}` : labelize(issue),
    reach: info.scope === 'Place' && cardCount > 1 ? `Blocks all ${cardCount} cards` : null,
  };
}

function taxonomyStatusForIssue(issue, taxonomyReview = {}) {
  const match = String(issue || '').match(/^(pending|unapproved)_(.+)$/);
  if (!match) return null;
  const [, status, field] = match;
  const review = taxonomyReview?.[field] || {};
  if (status === 'pending') return 'pending';
  const hasRetired = Array.isArray(review.retired) && review.retired.length > 0;
  const hasUnknown = Array.isArray(review.unknown) && review.unknown.length > 0;
  if (hasRetired && hasUnknown) return 'mixed';
  if (hasRetired) return 'retired';
  if (hasUnknown) return 'unknown';
  return 'unapproved';
}

function taxonomyActionForStatus(status) {
  if (status === 'mixed') return 'Review taxonomy blockers';
  if (status === 'retired') return 'Approve cleanup proposal';
  if (status === 'unknown') return 'Review unknown value';
  return 'Review in Taxonomy Librarian';
}

function taxonomyDetailForIssue(issue, taxonomyReview = {}) {
  const match = String(issue || '').match(/^(pending|unapproved)_(.+)$/);
  if (!match) return null;
  const [, status, field] = match;
  const review = taxonomyReview?.[field] || {};
  if (status === 'unapproved') {
    const details = [];
    if (Array.isArray(review.retired) && review.retired.length > 0) {
      details.push(`Retired taxonomy values: ${review.retired.map(value => labelize(value)).join(', ')}. Approve the tag cleanup proposal in Change Proposals, or rerun enrichment if no cleanup proposal is visible.`);
    }
    if (Array.isArray(review.unknown) && review.unknown.length > 0) {
      details.push(`Unknown taxonomy values: ${review.unknown.map(value => labelize(value)).join(', ')}. Review the card field or rerun enrichment if a cleanup proposal is missing.`);
    }
    if (details.length > 0) return details.join(' ');
  }
  const values = review?.[status];
  if (!Array.isArray(values) || values.length === 0) return null;
  const label = status === 'pending' ? 'Pending taxonomy values' : 'Unapproved taxonomy values';
  return `${label}: ${values.map(value => labelize(value)).join(', ')}`;
}

function queueFocusTab(card, viewId) {
  if (viewId === 'inbox') return 'review';
  if (viewId === 'ready_to_approve' || cardStage(card) === 'ready_to_approve') return 'review';
  if (viewId === 'approved' || viewId === 'published' || cardStage(card) === 'approved' || cardStage(card) === 'published') return 'versions';
  const state = readiness(card);
  if (state.missing[0]) return readinessIssueInfo(state.missing[0], 'missing', card?.card_count || 1, state.taxonomyReview).tab;
  if (state.blockers[0]) return readinessIssueInfo(state.blockers[0], 'blocker', card?.card_count || 1, state.taxonomyReview).tab;
  return 'card';
}

function queueFocusField(card) {
  const state = readiness(card);
  const issue = state.missing[0] || state.blockers[0];
  if (!issue) return null;
  const type = state.missing[0] ? 'missing' : 'blocker';
  return readinessIssueInfo(issue, type, card?.card_count || 1, state.taxonomyReview).focus || null;
}

function ReadinessIssueRow({ issue, type, cardCount, taxonomyReview, onOpen }) {
  const info = readinessIssueInfo(issue, type, cardCount, taxonomyReview);
  const taxonomyDetail = taxonomyDetailForIssue(issue, taxonomyReview);
  return (
    <button
      type="button"
      onClick={() => onOpen(info.tab, info.focus || null)}
      className="grid gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-left text-sm md:grid-cols-[80px_1fr_auto]"
    >
      <Badge kind={info.scope === 'Place' ? 'purple' : 'accent'}>{info.scope}</Badge>
      <span>
        <span className="block text-stone-800">{info.label}</span>
        {taxonomyDetail && <span className="block text-xs text-stone-500">{taxonomyDetail}</span>}
        {info.reach && <span className="text-xs text-stone-500">{info.reach}</span>}
      </span>
      <span className="text-blue-700">{info.action}</span>
    </button>
  );
}

function Section({ eyebrow, title, children }) {
  return (
    <section className="rounded-xl border border-stone-200 bg-white p-4">
      {eyebrow && <p className="mb-1 text-xs text-stone-400">{eyebrow}</p>}
      {title && <h3 className="mb-3 text-sm font-medium text-stone-900">{title}</h3>}
      {children}
    </section>
  );
}

const CONTACT_TYPE_OPTIONS = ['phone', 'email', 'website', 'social', 'booking', 'other'];
const SOURCE_TYPE_OPTIONS = ['admin_source', 'official_site', 'website', 'social', 'directory', 'other'];
const RECORD_STATUS_OPTIONS = ['active', 'retired'];
const TRUST_LEVEL_OPTIONS = ['official', 'operator_supplied', 'admin_current_best', 'supporting', 'unverified'];
const MANUAL_SOURCE_TYPE_OPTIONS = [
  'official_site_manual',
  'official_social_manual',
  'official_menu_manual',
  'official_event_manual',
  'official_booking_manual',
];
const MANUAL_CAPTURE_REASON_OPTIONS = [
  'bot_challenge',
  'age_gate',
  'image_only_menu',
  'blocked_embed',
  'pdf_or_menu_capture',
  'other',
];

function editorOptions(options, current) {
  return current && !options.includes(current) ? [...options, current] : options;
}

function EditorInput({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11px] font-medium text-stone-500">{label}</span>
      <input
        type={type}
        value={value || ''}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-stone-300 bg-white px-2.5 py-2 text-sm focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
      />
    </label>
  );
}

function EditorSelect({ label, value, options, onChange }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11px] font-medium text-stone-500">{label}</span>
      <select
        value={value || ''}
        onChange={event => onChange(event.target.value)}
        className="w-full rounded-md border border-stone-300 bg-white px-2.5 py-2 text-sm focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
      >
        {editorOptions(options, value).map(option => <option key={option} value={option}>{labelize(option)}</option>)}
      </select>
    </label>
  );
}

function EditorActions({ saving, disabled = false, onSave, onCancel }) {
  return (
    <div className="flex justify-end gap-2">
      <button type="button" onClick={onCancel} disabled={saving} aria-label="Cancel editing" title="Cancel" className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-stone-300 bg-white text-stone-600 hover:bg-stone-50 disabled:opacity-50">
        <X className="h-4 w-4" />
      </button>
      <button type="button" onClick={onSave} disabled={saving || disabled} className="inline-flex items-center gap-1.5 rounded-md bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800 disabled:opacity-50">
        <Check className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

function PlaceContactSourceEditor({
  contacts,
  sources,
  savingKey,
  sourceFocusKey = null,
  sourceHighlight = false,
  onCreateContact,
  onUpdateContact,
  onDeleteContact,
  onCreateSource,
  onUpdateSource,
  onDeleteSource,
  onCreateManualSourceSnapshot,
}) {
  const [contactEditor, setContactEditor] = useState(null);
  const [contactDraft, setContactDraft] = useState(null);
  const [sourceEditor, setSourceEditor] = useState(null);
  const [sourceDraft, setSourceDraft] = useState(null);
  const [manualEditorOpen, setManualEditorOpen] = useState(false);
  const [manualDraft, setManualDraft] = useState({
    source_url: '',
    source_title: '',
    source_type: 'official_site_manual',
    capture_reason: 'bot_challenge',
    source_text: '',
    note: '',
  });

  const startContact = contact => {
    setContactEditor(contact?.id || 'new');
    setContactDraft(contact ? {
      contact_type: contact.contact_type,
      label: contact.label || '',
      value: contact.value || '',
      source_url: contact.source_url || '',
      is_primary: Boolean(contact.is_primary),
      status: contact.status || 'active',
      confidence: contact.confidence || '',
    } : {
      contact_type: 'phone',
      label: '',
      value: '',
      source_url: '',
      is_primary: false,
      status: 'active',
      confidence: 'operator_supplied',
    });
  };

  const startSource = source => {
    setSourceEditor(source?.id || 'new');
    setSourceDraft(source ? {
      source_type: source.source_type,
      source_label: source.source_label || '',
      source_url: source.source_url || '',
      trust_level: source.trust_level || '',
      status: source.status || 'active',
      is_current: Boolean(source.is_current),
    } : {
      source_type: 'admin_source',
      source_label: '',
      source_url: '',
      trust_level: 'operator_supplied',
      status: 'active',
      is_current: !sources.some(sourceItem => sourceItem.is_current),
    });
  };

  const saveContact = async () => {
    if (!contactDraft?.value?.trim()) return;
    try {
      if (contactEditor === 'new') await onCreateContact(contactDraft);
      else await onUpdateContact(contactEditor, contactDraft);
      setContactEditor(null);
      setContactDraft(null);
    } catch (_error) {
      return;
    }
  };

  const saveSource = async () => {
    if (!sourceDraft?.source_url?.trim()) return;
    try {
      if (sourceEditor === 'new') await onCreateSource(sourceDraft);
      else await onUpdateSource(sourceEditor, sourceDraft);
      setSourceEditor(null);
      setSourceDraft(null);
    } catch (_error) {
      return;
    }
  };

  const openManualSnapshot = () => {
    setManualEditorOpen(true);
    setManualDraft({
      source_url: sources.find(source => source.is_current)?.source_url || sources[0]?.source_url || '',
      source_title: sources.find(source => source.is_current)?.source_label || '',
      source_type: 'official_site_manual',
      capture_reason: 'bot_challenge',
      source_text: '',
      note: '',
    });
  };

  const saveManualSnapshot = async () => {
    if (!manualDraft.source_url.trim() || !manualDraft.source_title.trim() || !manualDraft.source_text.trim()) return;
    try {
      await onCreateManualSourceSnapshot(manualDraft);
      setManualEditorOpen(false);
      setManualDraft({
        source_url: '',
        source_title: '',
        source_type: 'official_site_manual',
        capture_reason: 'bot_challenge',
        source_text: '',
        note: '',
      });
    } catch (_error) {
      return;
    }
  };

  const removeContact = async contact => {
    if (!window.confirm(`Remove ${contact.label || labelize(contact.contact_type)}?`)) return;
    try {
      await onDeleteContact(contact.id);
      if (contactEditor === contact.id) setContactEditor(null);
    } catch (_error) {
      return;
    }
  };

  const removeSource = async source => {
    if (!window.confirm(`Remove ${source.source_label || labelize(source.source_type)}?`)) return;
    try {
      await onDeleteSource(source.id);
      if (sourceEditor === source.id) setSourceEditor(null);
    } catch (_error) {
      return;
    }
  };

  const contactForm = (key, draft) => (
    <div key={key} className="rounded-lg border border-blue-200 bg-blue-50/40 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <EditorSelect label="Type" value={draft.contact_type} options={CONTACT_TYPE_OPTIONS} onChange={value => setContactDraft(current => ({ ...current, contact_type: value }))} />
        <EditorInput label="Label" value={draft.label} onChange={value => setContactDraft(current => ({ ...current, label: value }))} placeholder="Reservations, front desk" />
        <div className="sm:col-span-2"><EditorInput label="Value" value={draft.value} onChange={value => setContactDraft(current => ({ ...current, value }))} /></div>
        <div className="sm:col-span-2"><EditorInput label="Evidence URL" type="url" value={draft.source_url} onChange={value => setContactDraft(current => ({ ...current, source_url: value }))} /></div>
        <EditorSelect label="Status" value={draft.status} options={RECORD_STATUS_OPTIONS} onChange={value => setContactDraft(current => ({ ...current, status: value, is_primary: value === 'retired' ? false : current.is_primary }))} />
        <EditorSelect label="Confidence" value={draft.confidence} options={TRUST_LEVEL_OPTIONS} onChange={value => setContactDraft(current => ({ ...current, confidence: value }))} />
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs text-stone-700">
        <input type="checkbox" checked={Boolean(draft.is_primary)} disabled={draft.status === 'retired'} onChange={event => setContactDraft(current => ({ ...current, is_primary: event.target.checked }))} className="h-4 w-4 rounded border-stone-300 disabled:opacity-50" />
        Primary for this contact type
      </label>
      <div className="mt-3"><EditorActions saving={savingKey === `contact:${contactEditor}`} disabled={!draft.value.trim()} onSave={saveContact} onCancel={() => { setContactEditor(null); setContactDraft(null); }} /></div>
    </div>
  );

  const sourceForm = (key, draft) => (
    <div key={key} className="rounded-lg border border-blue-200 bg-blue-50/40 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <EditorSelect label="Type" value={draft.source_type} options={SOURCE_TYPE_OPTIONS} onChange={value => setSourceDraft(current => ({ ...current, source_type: value }))} />
        <EditorInput label="Label" value={draft.source_label} onChange={value => setSourceDraft(current => ({ ...current, source_label: value }))} placeholder="Official website" />
        <div className="sm:col-span-2"><EditorInput label="URL" type="url" value={draft.source_url} onChange={value => setSourceDraft(current => ({ ...current, source_url: value }))} /></div>
        <EditorSelect label="Trust" value={draft.trust_level} options={TRUST_LEVEL_OPTIONS} onChange={value => setSourceDraft(current => ({ ...current, trust_level: value }))} />
        <EditorSelect label="Status" value={draft.status} options={RECORD_STATUS_OPTIONS} onChange={value => setSourceDraft(current => ({ ...current, status: value, is_current: value === 'retired' ? false : current.is_current }))} />
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs text-stone-700">
        <input type="checkbox" checked={Boolean(draft.is_current)} disabled={draft.status === 'retired'} onChange={event => setSourceDraft(current => ({ ...current, is_current: event.target.checked }))} className="h-4 w-4 rounded border-stone-300 disabled:opacity-50" />
        Current source for published cards
      </label>
      <div className="mt-3"><EditorActions saving={savingKey === `source:${sourceEditor}`} disabled={!draft.source_url.trim()} onSave={saveSource} onCancel={() => { setSourceEditor(null); setSourceDraft(null); }} /></div>
    </div>
  );

  return (
    <Section title="Contacts and Sources">
      <div className="space-y-6">
        <div className="min-w-0">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h4 className="text-xs font-medium uppercase text-stone-500">Contacts · {contacts.length}</h4>
            <button type="button" onClick={() => startContact(null)} disabled={savingKey !== null || contactEditor === 'new'} className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs text-stone-700 hover:bg-stone-50 disabled:opacity-50">
              <Plus className="h-3.5 w-3.5" /> Add contact
            </button>
          </div>
          <div className="space-y-2">
            {contacts.length === 0 && contactEditor !== 'new' && <p className="py-3 text-sm text-stone-500">No contact records.</p>}
            {contactEditor === 'new' && contactDraft && contactForm('new-contact', contactDraft)}
            {contacts.map(contact => contactEditor === contact.id && contactDraft ? contactForm(contact.id, contactDraft) : (
              <div key={contact.id} className={`flex items-start gap-3 rounded-lg border border-stone-200 p-3 ${contact.status === 'retired' ? 'bg-stone-50 opacity-70' : 'bg-white'}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="text-sm font-medium text-stone-800">{contact.label || labelize(contact.contact_type)}</p>
                    <Badge>{labelize(contact.contact_type)}</Badge>
                    {contact.is_primary && <Badge kind="success">Primary</Badge>}
                    {contact.status !== 'active' && <Badge>{labelize(contact.status)}</Badge>}
                  </div>
                  <p className="mt-1 break-words text-sm text-stone-700">{contact.value}</p>
                  {contact.source_url && <p className="mt-1 truncate text-xs text-blue-700">{contact.source_url}</p>}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button type="button" onClick={() => startContact(contact)} disabled={savingKey !== null} aria-label="Edit contact" title="Edit contact" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-500 hover:bg-stone-100 hover:text-stone-900 disabled:opacity-50"><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => removeContact(contact)} disabled={savingKey !== null} aria-label="Remove contact" title="Remove contact" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div data-focus={sourceFocusKey || undefined} className={`min-w-0 border-t border-stone-200 pt-6 ${sourceHighlight ? 'rounded-lg ring-2 ring-blue-300' : ''}`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h4 className="text-xs font-medium uppercase text-stone-500">Sources · {sources.length}</h4>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" onClick={openManualSnapshot} disabled={savingKey !== null || manualEditorOpen} className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                <Plus className="h-3.5 w-3.5" /> Manual snapshot
              </button>
              <button type="button" onClick={() => startSource(null)} disabled={savingKey !== null || sourceEditor === 'new'} className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                <Plus className="h-3.5 w-3.5" /> Add source
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {manualEditorOpen && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <EditorInput label="Source URL" type="url" value={manualDraft.source_url} onChange={value => setManualDraft(current => ({ ...current, source_url: value }))} />
                  <EditorInput label="Source title" value={manualDraft.source_title} onChange={value => setManualDraft(current => ({ ...current, source_title: value }))} placeholder="Official taproom page" />
                  <EditorSelect label="Source type" value={manualDraft.source_type} options={MANUAL_SOURCE_TYPE_OPTIONS} onChange={value => setManualDraft(current => ({ ...current, source_type: value }))} />
                  <EditorSelect label="Reason" value={manualDraft.capture_reason} options={MANUAL_CAPTURE_REASON_OPTIONS} onChange={value => setManualDraft(current => ({ ...current, capture_reason: value }))} />
                  <div className="sm:col-span-2">
                    <label className="block min-w-0">
                      <span className="mb-1 block text-[11px] font-medium text-stone-500">Official source text</span>
                      <textarea
                        rows={7}
                        value={manualDraft.source_text}
                        onChange={event => setManualDraft(current => ({ ...current, source_text: event.target.value }))}
                        placeholder="Paste the relevant official page text or facts exactly enough for enrichment evidence."
                        className="w-full rounded-md border border-stone-300 bg-white px-2.5 py-2 text-sm leading-5 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
                      />
                    </label>
                  </div>
                  <div className="sm:col-span-2"><EditorInput label="Internal note" value={manualDraft.note} onChange={value => setManualDraft(current => ({ ...current, note: value }))} placeholder="Why this capture was needed" /></div>
                </div>
                <div className="mt-3"><EditorActions saving={savingKey === 'manual-source-snapshot'} disabled={!manualDraft.source_url.trim() || !manualDraft.source_title.trim() || !manualDraft.source_text.trim()} onSave={saveManualSnapshot} onCancel={() => setManualEditorOpen(false)} /></div>
              </div>
            )}
            {sources.length === 0 && sourceEditor !== 'new' && <p className="py-3 text-sm text-stone-500">No source records.</p>}
            {sourceEditor === 'new' && sourceDraft && sourceForm('new-source', sourceDraft)}
            {sources.map(source => sourceEditor === source.id && sourceDraft ? sourceForm(source.id, sourceDraft) : (
              <div key={source.id} className={`flex items-start gap-3 rounded-lg border border-stone-200 p-3 ${source.status === 'retired' ? 'bg-stone-50 opacity-70' : 'bg-white'}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="text-sm font-medium text-stone-800">{source.source_label || labelize(source.source_type)}</p>
                    <Badge>{labelize(source.source_type)}</Badge>
                    {source.is_current && <Badge kind="success">Current</Badge>}
                    {source.status !== 'active' && <Badge>{labelize(source.status)}</Badge>}
                  </div>
                  <a href={source.source_url} target="_blank" rel="noreferrer" className="mt-1 block truncate text-sm text-blue-700 hover:underline">{source.source_url}</a>
                  {source.trust_level && <p className="mt-1 text-xs text-stone-500">Trust: {labelize(source.trust_level)}</p>}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button type="button" onClick={() => startSource(source)} disabled={savingKey !== null} aria-label="Edit source" title="Edit source" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-500 hover:bg-stone-100 hover:text-stone-900 disabled:opacity-50"><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => removeSource(source)} disabled={savingKey !== null} aria-label="Remove source" title="Remove source" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

function WorkbenchTabs({ active, onChange, reviewCount, versionCount, signalIssueCount }) {
  const tabs = [
    ['card', 'Content'],
    ['signals', 'Composer Signals', signalIssueCount, 'accent'],
    ['review', 'Readiness', reviewCount, 'warning'],
    ['versions', 'Versions', versionCount, 'accent'],
  ];
  return (
    <div className="flex items-center gap-1 overflow-x-auto border-b border-stone-200 px-5">
      {tabs.map(([id, label, count, kind, blockingCount]) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={`border-b-2 px-4 py-3 text-sm font-medium ${
            active === id ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-800'
          }`}
        >
          {label}
          {count > 0 && (
            <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] ${kind === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
              {blockingCount > 0 ? `${count} · ${blockingCount} blocking` : count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function ListRow({ card, active, onOpen }) {
  const cardCount = Number(card.card_count || 1);
  const countLabel = `${cardCount} ${cardCount === 1 ? 'card' : 'cards'}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`grid w-full grid-cols-[2fr_0.85fr_1.35fr_0.7fr_0.6fr] items-center gap-3 border-b border-stone-100 px-4 py-3 text-left text-sm hover:bg-stone-50 ${
        active ? 'bg-blue-50/70' : 'bg-white'
      }`}
    >
      <span>
        <span className="block font-medium text-blue-700">{card.place_name}</span>
        <span className="block truncate text-xs text-stone-500">{countLabel}</span>
        <span className="text-xs text-stone-400">{card.area || 'Unknown area'} · {labelize(card.retrieval_lane)} · {card.place_master_id}</span>
      </span>
      <span><Badge kind={stageBadgeKind(card)}>{stageLabel(card)}</Badge></span>
      <span className="min-w-0 truncate text-stone-600">{primaryBlocker(card)}</span>
      <span className="text-stone-500">{formatDate(card.approved_at || card.updated_at)}</span>
      <span className="text-stone-700">
        {cardInboxCount(card) > 0 ? `${cardInboxCount(card)}${cardBlockingProposalCount(card) > 0 ? ` · ${cardBlockingProposalCount(card)} blocking` : ''}` : '-'}
      </span>
    </button>
  );
}

export {
  PAGE_SIZE,
  NEW_CARD_KEY,
  DETAIL_PANEL_DEFAULT_WIDTH,
  VOCAB_FAMILIES,
  CARD_FIELDS,
  PLACE_FIELDS,
  FRESHNESS_RISK_OPTIONS,
  RETRIEVAL_LANE_OPTIONS,
  SAVED_VIEW_PRESETS,
  WORKER_COMMANDS,
  text,
  labelize,
  arrayValue,
  formatDate,
  readiness,
  cardStage,
  stageLabel,
  stageBadgeKind,
  stageReason,
  cardIdentity,
  cardInboxCount,
  cardBlockingProposalCount,
  getSavedView,
  cardMatchesSavedView,
  savedViewQueryParams,
  clampDetailPanelWidth,
  isEditableShortcutTarget,
  formFromCard,
  newCardDraft,
  formFromPlace,
  changedPayload,
  TextInput,
  TextArea,
  SelectField,
  MultiPicker,
  displayDiffValue,
  editableProposalValue,
  parseEditedProposalValue,
  publishedProjectionDiffs,
  DiffRows,
  CardTags,
  Badge,
  queueFocusTab,
  queueFocusField,
  ReadinessIssueRow,
  Section,
  PlaceContactSourceEditor,
  WorkbenchTabs,
  ListRow,
};
